/**
 * ForceUpdateModal - Blocking modal that forces user to update.
 *
 * Cannot be dismissed. User must update to continue using the app.
 * Used when the current version is below the minimum supported version.
 */
import { AlertTriangle } from 'lucide-react';
import { useUpdateStore } from '../store/updateStore';

export default function ForceUpdateModal() {
  const {
    showForceUpdateModal,
    appVersion,
    minimumVersion,
    latestVersion,
    updateMessage,
    applyUpdate,
  } = useUpdateStore();

  if (!showForceUpdateModal) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80">
      <div className="bg-surface rounded-3xl max-w-sm w-full overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-6 text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warning/10 flex items-center justify-center">
            <AlertTriangle size={32} className="text-warning" />
          </div>

          <h2 className="text-xl font-bold text-text">
            Update Required
          </h2>

          <p className="text-text-secondary mt-2">
            {updateMessage || 'Your version of Momento is no longer supported. Please update to continue.'}
          </p>

          {/* Version info */}
          <div className="mt-4 p-3 bg-background rounded-xl text-sm">
            <div className="flex justify-between text-text-muted">
              <span>Your version:</span>
              <span className="font-mono text-error">{appVersion}</span>
            </div>
            <div className="flex justify-between text-text-muted mt-1">
              <span>Required version:</span>
              <span className="font-mono text-text">{minimumVersion}+</span>
            </div>
            <div className="flex justify-between text-text-muted mt-1">
              <span>Latest version:</span>
              <span className="font-mono text-success">{latestVersion}</span>
            </div>
          </div>
        </div>

        {/* Update button - cannot dismiss */}
        <div className="p-4 border-t border-border">
          <button
            onClick={applyUpdate}
            className="w-full py-4 bg-primary text-on-primary font-bold rounded-xl hover:bg-primary-hover transition-colors active:scale-[0.98]"
          >
            Update Momento
          </button>
          <p className="text-xs text-text-muted text-center mt-2">
            This will refresh the app with the latest version
          </p>
        </div>
      </div>
    </div>
  );
}
